/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ticketColors } from '../../../../shared/theme/ticketColors'
import { spacing } from '../../../../shared/theme/spacing';
import { ModuleEmptyState } from '../../../../shared/ui/ModuleEmptyState';
import { empPermStyles } from './employeePermissions.styles';
import { OperationalFilterPickerRow } from '../../../../shared/filters/OperationalFilterPrimitives'
import { MasterFilterSheetModal } from '../../../../shared/filters/MasterFilterSheetModal'
import type { MasterFilterOption } from '../../../../shared/filters/MasterFilterSheetModal';
import type { EmployeeRecord } from '../../../store/useEmployeesStore';
import type { ModuleKey } from '../../../../shell/session/store/sessionTypes';
import type { EmployeePermissionsVm } from './useEmployeePermissions';
import { resolveBranchDisplayName } from './useEmployeePermissions';
import { MODULE_LABELS_ES, MODULE_SHORT_LABELS_ES } from './permissionUiLabels';
import { EmployeePermissionsMatrixLegend } from './EmployeePermissionsMatrixLegend';
import { invitationRoleLabel } from './EmployeeRolePresets';

type Props = {
  vm: EmployeePermissionsVm;
};

export function EmployeePermissionsMatrix({ vm }: Props) {
  const { filteredActiveEmployees, branches, branchNameById, visibleModules, togglePermissionSafe, busyKey } = vm;
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(
    branches[0]?.id ?? null
  );
  const [branchSheetOpen, setBranchSheetOpen] = useState(false);
  const [useFullModuleNames, setUseFullModuleNames] = useState(false);

  useEffect(() => {
    if (!selectedBranchId && branches[0]) {
      setSelectedBranchId(branches[0].id);
    }
  }, [branches, selectedBranchId]);

  const branchOptions: MasterFilterOption[] = useMemo(
    () => branches.map((b) => ({ id: b.id, name: b.name })),
    [branches]
  );

  const selectedBranchLabel = resolveBranchDisplayName(
    branchNameById,
    selectedBranchId ?? '',
    'Seleccionar sucursal'
  );

  if (filteredActiveEmployees.length === 0) {
    return (
      <ModuleEmptyState
        title="Sin resultados"
        subtitle="No hay empleados activos con estos filtros."
        testID="perfil.team.permissions.no-results"
      />
    );
  }

  return (
    <View style={{ gap: spacing.sm }}>
      <View style={empPermStyles.matrixBranchSelector}>
        <Text style={empPermStyles.matrixBranchSelectorLabel}>Mostrando permisos para:</Text>
        <OperationalFilterPickerRow
          label="Sucursal"
          valueLabel={selectedBranchLabel}
          onPress={() => setBranchSheetOpen(true)}
          active={true}
          accessibilityLabel="Seleccionar sucursal para matriz"
        />
      </View>

      <EmployeePermissionsMatrixLegend
        useFullModuleNames={useFullModuleNames}
        onToggleModuleNames={() => setUseFullModuleNames((v) => !v)}
      />

      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <View style={empPermStyles.matrixContainer}>
          <View style={empPermStyles.matrixHeaderRow}>
            <View style={[empPermStyles.matrixHeaderCell, empPermStyles.matrixHeaderCellFirst]}>
              <Text style={[empPermStyles.matrixHeaderText, empPermStyles.matrixHeaderTextFirst]}>
                Empleado
              </Text>
            </View>
            {visibleModules.map((module: ModuleKey) => {
              const fullName = MODULE_LABELS_ES[module] ?? module;
              const shortName = MODULE_SHORT_LABELS_ES[module] ?? module;
              return (
                <View key={module} style={empPermStyles.matrixHeaderCell}>
                  <Text
                    style={empPermStyles.matrixHeaderText}
                    accessibilityLabel={fullName}
                    numberOfLines={useFullModuleNames ? 2 : 1}
                  >
                    {useFullModuleNames ? fullName : shortName}
                  </Text>
                </View>
              );
            })}
          </View>

          {filteredActiveEmployees.map((employee: EmployeeRecord, empIdx: number) => {
            const assignment = employee.branchAssignments.find(
              (a) => a.branchId === selectedBranchId
            );
            const isLast = empIdx === filteredActiveEmployees.length - 1;
            return (
              <View
                key={employee.id}
                style={[empPermStyles.matrixRow, isLast && empPermStyles.matrixRowLast]}
              >
                <View style={[empPermStyles.matrixCell, empPermStyles.matrixCellFirst]}>
                  <Text style={empPermStyles.matrixCellText} numberOfLines={1}>
                    {employee.name}
                  </Text>
                  <Text style={empPermStyles.matrixCellSub} numberOfLines={1}>
                    {invitationRoleLabel(employee.role)}
                  </Text>
                </View>
                {visibleModules.map((module: ModuleKey) => {
                  const perm = assignment?.permissions.find((p) => p.module === module);
                  // La membresía owner no usa EBP: tiene acceso total y no se edita.
                  const isOwnerRow = employee.isOwner === true;
                  const canView = isOwnerRow ? true : perm?.canView ?? false;
                  const canEdit = isOwnerRow ? true : perm?.canEdit ?? false;
                  const isBusy = busyKey === `toggle-${employee.id}-${selectedBranchId}-${module}`;

                  const iconColor = canEdit
                    ? ticketColors.gold
                    : canView
                      ? ticketColors.ink2
                      : ticketColors.ink3;
                  const label = canEdit ? 'E' : canView ? 'V' : '—';

                  return (
                    <TouchableOpacity
                      key={module}
                      style={empPermStyles.matrixCell}
                      onPress={() => {
                        // 3-state cycle: none → view → edit → none
                        const field: 'canView' | 'canEdit' = canEdit
                          ? 'canView'
                          : canView
                            ? 'canEdit'
                            : 'canView';
                        togglePermissionSafe(
                          employee.id,
                          selectedBranchId ?? '',
                          module,
                          field,
                        );
                      }}
                      disabled={!selectedBranchId || isBusy || isOwnerRow}
                      accessibilityRole="button"
                      accessibilityLabel={`${employee.name} ${MODULE_SHORT_LABELS_ES[module] ?? module}: ${
                        canEdit ? 'editar' : canView ? 'solo lectura' : 'sin acceso'
                      }`}
                    >
                      <View
                        style={[
                          empPermStyles.matrixPermIcon,
                          canEdit
                            ? empPermStyles.matrixPermIconEdit
                            : canView
                              ? empPermStyles.matrixPermIconView
                              : empPermStyles.matrixPermIconNone,
                        { borderColor: canView && !canEdit ? iconColor : undefined },
                        ]}
                      >
                        <Text style={[empPermStyles.matrixPermLabel, { color: iconColor }]}>
                          {label}
                        </Text>
                      </View>
                    </TouchableOpacity>
                  );
                })}
              </View>
            );
          })}
        </View>
      </ScrollView>

      <MasterFilterSheetModal
        visible={branchSheetOpen}
        title="Seleccionar sucursal"
        options={branchOptions}
        selectedId={selectedBranchId}
        onSelect={setSelectedBranchId}
        onClose={() => setBranchSheetOpen(false)}
        showAllOption={false}
      />
    </View>
  );
}
